import React, { useEffect, useMemo, useState } from 'react';

import API_BASE from '../config';
import AuthService from '../services/auth.service';
import ProductService from '../services/product.service';
import Spinner from './Spinner';
import TableService from '../services/table.service';
import { formatCurrency } from '../common/utils';
import { normalizeText } from '../utils/search';
import { isOffensiveText } from '../utils/usernameFilter';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const errMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message;

// Resolve a product image URL the same way Catalog does.
const imgUrl = (p) => {
  if (!p || !p.image_url) return null;
  if (/^https?:\/\//i.test(p.image_url)) return p.image_url;
  const clean = p.image_url.replace(/^uploads\//, '/');
  return API_BASE + (clean.startsWith('/') ? clean : '/' + clean);
};

// Case/accent-insensitive (shared helper; đ -> d).
const norm = normalizeText;

// Text a product can be matched on: name (+ translations) and catalog names.
const searchText = (p) =>
  norm(
    [
      ...Object.keys(p)
        .filter((k) => k === 'name' || k.startsWith('name_'))
        .map((k) => p[k]),
      ...['catalog', 'sub_catalog'].flatMap((c) =>
        p[c]
          ? Object.keys(p[c])
              .filter((k) => k === 'name' || k.startsWith('name_'))
              .map((k) => p[c][k])
          : [],
      ),
    ].join(' '),
  );

const PAGE_SIZE = 100; // server max per page

// Load every orderable-by-table product (all pages), not just the first 100.
const fetchAllTableProducts = async () => {
  const first = await ProductService.getAll({ page: 1, size: PAGE_SIZE, for: 'table' });
  let items = first.data.items || [];
  const totalPages = first.data.totalPages || 1;
  for (let page = 2; page <= totalPages; page++) {
    const res = await ProductService.getAll({ page, size: PAGE_SIZE, for: 'table' });
    items = items.concat(res.data.items || []);
  }
  return items;
};

// Display name of the logged-in user (null for guests / expired sessions).
const loggedInName = () => {
  const u = AuthService.getCurrentUser();
  return (u && (u.username || u.email)) || null;
};

// Public page: /order-by-table/:tableId — order without logging in.
// Guarded server-side by the "order_by_table" feature flag.
const OrderByTable = () => {
  const { t } = useTranslation();
  const { tableId } = useParams();

  const [table, setTable] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState({}); // { [productId]: count }
  // Logged-in user: name is prefilled from the account and locked.
  const [userName, setUserName] = useState(loggedInName);
  const [customerName, setCustomerName] = useState(() => loggedInName() || '');
  const [note, setNote] = useState('');
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [search, setSearch] = useState('');

  // Keep in sync if the user logs in/out in another tab.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'user' && e.key !== null) return;
      const name = loggedInName();
      setUserName(name);
      setCustomerName(name || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    TableService.getPublicTable(tableId)
      .then((res) => {
        if (!alive) return;
        setTable(res.data);
        // Load the whole menu so the search covers all products.
        return fetchAllTableProducts();
      })
      .then((items) => {
        if (alive && items) setProducts(items);
      })
      .catch((err) => {
        if (alive) setError(errMsg(err));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tableId]);

  const setCount = (pid, n) =>
    setQty((q) => {
      const next = { ...q };
      if (n <= 0) delete next[pid];
      else next[pid] = Math.min(n, 99);
      return next;
    });

  const lines = useMemo(() => products.filter((p) => qty[p.id] > 0).map((p) => ({ ...p, quantity: qty[p.id], line_total: Number(p.price || 0) * qty[p.id] })), [products, qty]);
  const indexed = useMemo(() => products.map((p) => ({ p, text: searchText(p) })), [products]);
  const visibleProducts = useMemo(() => {
    const terms = norm(search).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return products;
    return indexed.filter(({ text }) => terms.every((term) => text.includes(term))).map(({ p }) => p);
  }, [indexed, products, search]);
  const total = lines.reduce((s, l) => s + l.line_total, 0);
  const noteBad = useMemo(() => isOffensiveText(note), [note]);

  const submit = (e) => {
    e.preventDefault();
    if (lines.length === 0) {
      toast.error(t('orderByTable.emptyOrder'));
      return;
    }
    if (isOffensiveText(note)) {
      toast.error(t('orderByTable.noteNotAllowed'));
      return;
    }
    setPlacing(true);
    TableService.placeOrder(tableId, {
      items: lines.map((l) => ({ product_id: l.id, quantity: l.quantity })),
      customer_name: (userName || customerName).trim() || undefined,
      note: note.trim() || undefined,
    })
      .then((res) => {
        setPlaced(res.data);
        setQty({});
        setCustomerName(userName || '');
        setNote('');
        toast.success(t('orderByTable.placed'));
      })
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setPlacing(false));
  };

  if (loading)
    return (
      <div className='container mt-5'>
        <Spinner />
      </div>
    );

  if (error) {
    return (
      <div className='container mt-5'>
        <div className='alert alert-danger'>{error}</div>
      </div>
    );
  }

  if (placed) {
    return (
      <div className='container mt-5' style={{ maxWidth: 640 }}>
        <div className='card shadow-sm'>
          <div className='card-body text-center'>
            <h3 className='text-success'>{t('orderByTable.thankYou')}</h3>
            <p className='mb-1'>{t('orderByTable.orderPlacedFor', { table: placed.table_name })}</p>
            <p className='text-muted'>
              {t('orderByTable.orderNumber', { id: placed.id })} · {formatCurrency(placed.total_amount)}
            </p>
            <ul className='list-group list-group-flush text-start my-3'>
              {placed.items.map((it) => (
                <li className='list-group-item d-flex justify-content-between' key={it.id}>
                  <span>
                    {it.product_name} × {it.quantity}
                  </span>
                  <span>{formatCurrency(it.line_total)}</span>
                </li>
              ))}
            </ul>
            <button className='btn btn-outline-primary' onClick={() => setPlaced(null)}>
              {t('orderByTable.orderMore')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container mt-4' style={{ maxWidth: 820 }}>
      <div className='mb-3'>
        <h3 className='mb-0'>{t('orderByTable.title')}</h3>
        <span className='badge bg-primary fs-6 mt-2'>{t('orderByTable.tableLabel', { name: table.name })}</span>
      </div>

      <form onSubmit={submit}>
        {products.length > 0 && (
          <div className='input-group mb-3'>
            <span className='input-group-text'>🔍</span>
            <input type='search' className='form-control' placeholder={t('orderByTable.searchPlaceholder')} aria-label={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()} />
            {search && (
              <button type='button' className='btn btn-outline-secondary' onClick={() => setSearch('')}>
                {t('common.clear')}
              </button>
            )}
          </div>
        )}
        {search && products.length > 0 && <p className='text-muted small mb-2'>{t('orderByTable.searchCount', { count: visibleProducts.length, total: products.length })}</p>}
        <div className='row'>
          {products.length === 0 && <p className='text-muted'>{t('orderByTable.noProducts')}</p>}
          {products.length > 0 && visibleProducts.length === 0 && <p className='text-muted'>{t('common.noResults')}</p>}
          {visibleProducts.map((p) => {
            const src = imgUrl(p);
            return (
              <div className='col-12 col-sm-6 mb-3' key={p.id}>
                <div className='card h-100'>
                  <div className='card-body d-flex gap-3'>
                    {src && <img src={src} alt={p.name} width='64' height='64' style={{ objectFit: 'cover', borderRadius: 8 }} />}
                    <div className='flex-grow-1'>
                      <h6 className='mb-1'>{p.name}</h6>
                      <div className='text-muted small mb-2'>{formatCurrency(p.price)}</div>
                      <div className='input-group input-group-sm' style={{ maxWidth: 140 }}>
                        <button type='button' className='btn btn-outline-secondary' onClick={() => setCount(p.id, (qty[p.id] || 0) - 1)}>
                          −
                        </button>
                        <input type='number' min='0' max='99' className='form-control text-center' value={qty[p.id] || 0} onChange={(e) => setCount(p.id, parseInt(e.target.value, 10) || 0)} />
                        <button type='button' className='btn btn-outline-secondary' onClick={() => setCount(p.id, (qty[p.id] || 0) + 1)}>
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className='card shadow-sm mt-2'>
          <div className='card-body'>
            <h5 className='card-title'>{t('orderByTable.yourOrder')}</h5>
            {lines.length === 0 ? (
              <p className='text-muted mb-3'>{t('orderByTable.emptyOrder')}</p>
            ) : (
              <ul className='list-group list-group-flush mb-3'>
                {lines.map((l) => (
                  <li className='list-group-item d-flex justify-content-between px-0' key={l.id}>
                    <span>
                      {l.name} × {l.quantity}
                    </span>
                    <span>{formatCurrency(l.line_total)}</span>
                  </li>
                ))}
                <li className='list-group-item d-flex justify-content-between px-0 fw-bold'>
                  <span>{t('orderByTable.total')}</span>
                  <span>{formatCurrency(total)}</span>
                </li>
              </ul>
            )}

            <div className='row g-2 mb-3'>
              <div className='col-md-12'>
                <input type='text' className='form-control' placeholder={t('orderByTable.namePlaceholder')} value={userName || customerName} maxLength={150} disabled={!!userName} title={userName ? t('orderByTable.nameFromAccount') : undefined} onChange={(e) => setCustomerName(e.target.value)} />
              </div>
              <div className='col-md-12'>
                <textarea type='text' className={'form-control' + (noteBad ? ' is-invalid' : '')} placeholder={t('orderByTable.notePlaceholder')} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
                {noteBad && <div className='invalid-feedback'>{t('orderByTable.noteNotAllowed')}</div>}
              </div>
            </div>

            <button className='btn btn-primary w-100' type='submit' disabled={placing || lines.length === 0}>
              {placing ? t('common.saving') : t('orderByTable.placeOrder')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OrderByTable;
